
import React, { useEffect, useState } from 'react';
import { store } from '../../../app/store';
import { postAPI } from '../../../app/api';
import { toast } from 'react-toastify';
import { hasPermission, isAdmin } from '../../../store/hooks/Permission';
import './tooltip.css';
import Excel from '../../../components/Excel/Excel';
import { DownloadOutlined } from '@ant-design/icons';

// List component for Users page

export default function List({ setIsEdit, setEditId, setShowPermission, setPermissionUserId }) {
    const store_data = JSON.parse(store());
    const [users, setUsers] = useState([]);
    
    // Permission states
    const [canView, setCanView] = useState(false);
    const [canEdit, setCanEdit] = useState(false);
    const [canDelete, setCanDelete] = useState(false);
    const [canManagePermissions, setCanManagePermissions] = useState(false);
    const [canExport, setCanExport] = useState(false);

    useEffect(() => {
        // Check permissions first
        const checkPermissions = async () => {
            if (isAdmin()) {
                setCanView(true);
                setCanEdit(true);
                setCanDelete(true);
                setCanManagePermissions(true);
                setCanExport(true);
                fetchUsersCreds();
            } else {
                const viewAllowed = await hasPermission("ADMIN", "Users", "VIEW");
                const editAllowed = await hasPermission("ADMIN", "Users", "EDIT");
                const deleteAllowed = await hasPermission("ADMIN", "Users", "DELETE");
                const permissionsAllowed = await hasPermission("ADMIN", "Users", "PERMISSIONS");
                const exportAllowed = await hasPermission("ADMIN", "Users", "EXPORT");
                
                setCanView(viewAllowed);
                setCanEdit(editAllowed);
                setCanDelete(deleteAllowed);
                setCanManagePermissions(permissionsAllowed);
                setCanExport(exportAllowed);
                
                if (viewAllowed) {
                    fetchUsersCreds();
                } else {
                    setUsers([]);
                    toast.error('You do not have permission to view this page.');
                }
            }
        };
        
        checkPermissions();
        // eslint-disable-next-line
    }, []);

    const fetchUsersCreds = async () => {
        const filter = { org_id: store_data?.org_id };
        const response = await postAPI('/users/list', {...filter, user: store_data});
        if (response?.status === 1) {
            setUsers(response?.data);
        } else {
            setUsers([]);
        }
    };

    // Status toggle
    const handleChangeStatus = async (username, status) => {
        if (!canEdit) {
            toast.error('You do not have permission to perform this action');
            return;
        }
        const filter = { org_id: store_data?.org_id, username, status };
        const response = await postAPI('/users/toggle-status', {...filter, user: store_data});
        if (response?.status === 1) {
            toast.success('Status Changed Successfully.');
            fetchUsersCreds();
        } else {
            toast.error('Sorry ! Something Wrong. Please try to contact administrator.');
        }
    };

    // Remove user
    const handleRemove = async username => {
        if (!canDelete) {
            toast.error('You do not have permission to perform this action');
            return;
        }
        const filter = { org_id: store_data?.org_id, username };
        const response = await postAPI('/users/remove', {...filter, user: store_data});
        if (response?.status === 1) {
            toast.success('Users Credential Removed Successfully.');
            fetchUsersCreds();
        } else {
            toast.error('Sorry ! Something Wrong. Please try to contact administrator.');
        }
    };

    // Render
    return (
        <table className="table card-table">
            <thead>
                <tr>
                    <th>Role</th>
                    <th>Name</th>
                    <th>Designation</th>
                    <th>Email</th>
                    <th>Contact Number</th>
                    <th style={{ width: '100px' }}>Status</th>
                    <th style={{ width: '220px' }}>Actions</th>
                </tr>
            </thead>
            <tbody className="table-border-bottom-0">
                {!canView ? (
                    <tr>
                        <td colSpan="6" className="text-center text-danger py-4">
                            You do not have permission to view this page.
                        </td>
                    </tr>
                ) : users && users.length > 0 ? users.map((val, index) => val?.role === 'CUSTOMER' ? null : (
                    <tr key={val?.id || index}>
                        <td><span className="badge bg-label-info">{val?.role}</span></td>
                        <td><strong>{val?.name}</strong></td>
                        <td>{val?.designation}</td>
                        <td>{val?.email}</td>
                        <td>{val?.contact_number}</td>
                        <td>
                            {canEdit ? (
                                <span
                                    className={`badge ${val?.status === 1 ? 'bg-label-success' : 'bg-label-secondary'} me-1 cursor-pointer custom-tooltip`}
                                    tabIndex={0}
                                    onClick={() => handleChangeStatus(val?.username, !val?.status)}
                                >
                                    {val?.status === 1 ? 'Active' : 'Inactive'}
                                    <span className="tooltip-text">Toggle Status</span>
                                </span>
                            ) : (
                                <span className={`badge ${val?.status === 1 ? 'bg-label-success' : 'bg-label-secondary'} me-1`}>
                                    {val?.status === 1 ? 'Active' : 'Inactive'}
                                </span>
                            )}
                        </td>
                        <td>
                            {canEdit ? (
                                <>
                                    <span
                                        className="badge bg-label-primary me-1 cursor-pointer custom-tooltip"
                                        tabIndex={0}
                                        data-bs-toggle="modal"
                                        data-bs-target="#Users"
                                        onClick={() => { setIsEdit(true); setEditId(val?.id); }}
                                    >
                                        <i className="fa fa-pen"></i>
                                        <span className="tooltip-text">Edit User</span>
                                    </span>
                                </>
                            ) : (
                                <span className="badge bg-label-secondary me-1">
                                    <i className="fa fa-lock"></i> No Edit Access
                                </span>
                            )}
                            
                            {canDelete && (
                                <span
                                    className="badge bg-label-danger me-1 cursor-pointer custom-tooltip"
                                    tabIndex={0}
                                    onClick={() => {
                                        if (window.confirm('Are you sure? This action will permanently remove this data!')) {
                                            handleRemove(val?.username);
                                        }
                                    }}
                                >
                                    <i className="fa fa-trash"></i>
                                    <span className="tooltip-text">Delete User</span>
                                </span>
                            )}
                            
                            {val?.role !== 'ADMIN' && canManagePermissions && (
                                <span
                                    className="badge bg-label-warning me-1 cursor-pointer custom-tooltip"
                                    tabIndex={0}
                                    onClick={() => {
                                        setPermissionUserId(val?.id);
                                        setShowPermission(true);
                                    }}
                                >
                                    <i className="fa fa-lock"></i>
                                    <span className="tooltip-text">Manage Permissions</span>
                                </span>
                            )}
                        </td>
                    </tr>
                )) : (
                    <tr>
                        <td colSpan="6" className="text-center py-4">
                            <p className="text-muted mb-0">No users found</p>
                        </td>
                    </tr>
                )}
            </tbody>
        </table>
    );
}
